/**
 * Sovereign Emerald tokens for the unified OMS Operations Dashboard.
 * Kept in sync with the EPC PMO register (pmoTheme.ts) — cream banknote
 * paper, racing-green ink, champagne metallic accent. Flat printed surfaces.
 */
import type { ThemeConfig } from 'antd';
import type { Variants } from 'framer-motion';
import type { OmsLayerKpiHealth } from './types';

export const OMS_COLORS = {
  teal: '#1F5A47',
  tealDark: '#0B3B2E',
  mint: '#339274',
  amber: '#C9A961',
  champagneDeep: '#8A5A08',
  red: '#9F1239',
  slate: '#5A6275',
  canvas: '#F2EADB',
  ink: '#2A1F17',
  caption: '#7C8A82',
} as const;

/** Card shell — engraved ivory plate lifted off the cream canvas. */
export const CARD_CLASS =
  'relative rounded-2xl border border-[rgba(11,59,46,0.28)] bg-[rgba(250,247,238,0.96)] shadow-[0_1px_2px_rgba(11,59,46,0.04),0_18px_40px_-24px_rgba(42,31,23,0.28)]';

/** Section eyebrow label. */
export const EYEBROW_CLASS = 'text-[10px] font-bold uppercase tracking-[0.18em] text-[#1F5A47]';

export const sectionVariants: Variants = {
  hidden: { opacity: 0, y: 14 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: (i as number) * 0.05, duration: 0.45 },
  }),
};

/**
 * Antd ConfigProvider theme for dashboard filter controls (Select, DatePicker).
 * Mirrors SovereignDatePicker's register: ivory input fill, racing-green ink,
 * champagne-tinted selected options, lime focus ring.
 */
export const SOVEREIGN_CONTROL_THEME: ThemeConfig = {
  token: {
    colorPrimary: OMS_COLORS.teal,
    colorBgElevated: '#FCFBF6',
    colorBgContainer: '#fcfaf3',
    colorText: OMS_COLORS.tealDark,
    colorTextPlaceholder: OMS_COLORS.caption,
    colorTextQuaternary: OMS_COLORS.caption,
    colorBorder: 'rgba(11,59,46,0.28)',
    colorSplit: 'rgba(11,59,46,0.14)',
    borderRadius: 10,
    controlItemBgActive: 'rgba(201,169,97,0.18)',
    controlItemBgHover: 'rgba(11,59,46,0.06)',
    fontFamily: 'inherit',
  },
  components: {
    Select: {
      activeBorderColor: OMS_COLORS.tealDark,
      hoverBorderColor: 'rgba(51,146,116,0.6)',
      activeOutlineColor: 'rgba(166,206,57,0.22)',
      selectorBg: '#fcfaf3',
      optionSelectedBg: 'rgba(201,169,97,0.18)',
      optionSelectedColor: OMS_COLORS.tealDark,
      optionActiveBg: 'rgba(11,59,46,0.06)',
      optionSelectedFontWeight: 700,
    },
    DatePicker: {
      activeBorderColor: OMS_COLORS.tealDark,
      hoverBorderColor: 'rgba(51,146,116,0.6)',
      activeShadow: '0 0 0 3px rgba(166,206,57,0.22)',
      colorBgContainer: '#fcfaf3',
      cellHoverBg: 'rgba(11,59,46,0.06)',
      cellActiveWithRangeBg: 'rgba(51,146,116,0.12)',
    },
  },
};

/** Shared base ApexCharts options (no toolbar, inherit font, transparent bg). */
export const baseChartOptions = {
  chart: {
    toolbar: { show: false },
    fontFamily: 'inherit',
    background: 'transparent',
    foreColor: '#7C8A82',
    animations: { enabled: true, speed: 650 },
  },
  dataLabels: { enabled: false },
  tooltip: { theme: 'light' as const },
  legend: { fontSize: '11px', labels: { colors: '#2A1F17' } },
};

/** OMS KPI health → ink (text) and fill (bar) colours. */
export const KPI_HEALTH_COLOR: Record<OmsLayerKpiHealth, { ink: string; fill: string }> = {
  good: { ink: OMS_COLORS.teal, fill: OMS_COLORS.mint },
  attention: { ink: OMS_COLORS.champagneDeep, fill: OMS_COLORS.amber },
  critical: { ink: OMS_COLORS.red, fill: OMS_COLORS.red },
  neutral: { ink: OMS_COLORS.slate, fill: OMS_COLORS.slate },
};

/** Project risk chips (Command Center portfolio). */
export const RISK_COLOR: Record<'low' | 'medium' | 'high' | 'critical', { ink: string; fill: string; label: string }> = {
  low: { ink: OMS_COLORS.teal, fill: OMS_COLORS.mint, label: 'Low risk' },
  medium: { ink: OMS_COLORS.champagneDeep, fill: OMS_COLORS.amber, label: 'Medium' },
  high: { ink: OMS_COLORS.red, fill: '#B91C1C', label: 'High' },
  critical: { ink: OMS_COLORS.red, fill: OMS_COLORS.red, label: 'Critical' },
};

/** Insight card tones (Executive insights rail). */
export const INSIGHT_TONE_COLOR: Record<'positive' | 'warning' | 'critical' | 'neutral', { ink: string; fill: string }> = {
  positive: { ink: OMS_COLORS.teal, fill: OMS_COLORS.mint },
  warning: { ink: OMS_COLORS.champagneDeep, fill: OMS_COLORS.amber },
  critical: { ink: OMS_COLORS.red, fill: OMS_COLORS.red },
  neutral: { ink: OMS_COLORS.slate, fill: OMS_COLORS.slate },
};

/** Task status segments shared by portfolio bars, donuts and heatmaps. */
export const STATUS_SEGMENTS = [
  { key: 'passTasks', label: 'Pass', color: OMS_COLORS.mint },
  { key: 'failTasks', label: 'Fail', color: OMS_COLORS.red },
  { key: 'holdTasks', label: 'Hold', color: OMS_COLORS.amber },
  { key: 'dueTasks', label: 'Due', color: OMS_COLORS.champagneDeep },
  { key: 'inProgressTasks', label: 'In progress', color: OMS_COLORS.slate },
] as const;

/**
 * Per-engineer status segments (Workforce box + engineer drawer). Same hues
 * as STATUS_SEGMENTS so a status reads identically everywhere; overdue gets
 * its own crimson so it never blends into Fail's deep rose.
 */
export const RESOURCE_STATUS_SEGMENTS = [
  { key: 'pass', label: 'Pass', color: OMS_COLORS.mint },
  { key: 'fail', label: 'Fail', color: OMS_COLORS.red },
  { key: 'inprogress', label: 'In progress', color: OMS_COLORS.slate },
  { key: 'due', label: 'Due', color: OMS_COLORS.champagneDeep },
  { key: 'overdue', label: 'Overdue', color: '#B91C1C' },
  { key: 'hold', label: 'Hold', color: OMS_COLORS.amber },
] as const;

/** Hex + alpha suffix for tinted backgrounds (e.g. tint('#1F5A47', 0.12)). */
export function tint(hex: string, alpha: number): string {
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${a}`;
}
